<template>
  <SFAsyncDataWrapper :async-data="productAsyncData">
    <div v-if="product" class="xl:container md:pt-4 md:max-xl:mx-5">
      <div class="grid grid-cols-12 gap-1 md:gap-x-8">
        <div
          class="col-span-12 flex items-start max-md:w-full md:col-span-6 md:shrink"
        >
          <SFProductGallery v-if="product" :product="product" />
        </div>
        <div class="col-span-12 md:col-span-5">
          <SfProductDetailsLayout
            :product="product"
            :name="name"
            :brand="brand"
          />
        </div>
        <div class="col-span-11 hidden md:col-start-2 md:block">
          <SFProductDetails :product="product" />
        </div>
      </div>
      <hr v-if="recommendedProductIds.length" class="mb-10 md:hidden" />
      <SFProductRecommendations
        v-if="recommendedProductIds.length"
        class="max-md:px-5"
        :product-ids="recommendedProductIds"
        :title="$t('global.product_recommendation')"
      >
        <template #header="{ title: recommendationTitle }">
          <h2
            class="mb-6 truncate pr-20 text-2xl font-medium text-gray-900 md:px-2"
          >
            {{ recommendationTitle }}
          </h2>
        </template>
      </SFProductRecommendations>
      <SFRecentlyViewedProductsSlider class="mt-10 max-md:px-5" />
    </div>
    <template #loading>
      <SFProductDetailPageLoadingState />
    </template>
  </SFAsyncDataWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { whenever } from '@vueuse/core'
import {
  getFirstAttributeValue,
  sanitizeCanonicalURL,
} from '@scayle/storefront-nuxt'
import { useRecentlyViewedProducts } from '@scayle/storefront-product-detail/composables'
import { clearNuxtData } from '#app/composables/asyncData'
import { useRoute } from '#app/composables/router'
import { definePageMeta, useHead, useImage, useSeoMeta } from '#imports'
import { useNuxtApp } from '#app/nuxt'
import { createError } from '#app/composables/error'
import { useJsonld } from '~/composables/useJsonld'
import { useProduct, useCurrentShop } from '#storefront/composables'
import { useTrackingEvents } from '~/composables/useTrackingEvents'
import { useProductBaseInfo } from '~/composables/useProductBaseInfo'
import { useI18n } from '#i18n'
import useSizeSelector from '~/composables/useSizeSelector'
import { PRODUCT_DETAIL_WITH_PARAMS } from '~/constants'
import SFAsyncDataWrapper from '~/components/SFAsyncDataWrapper.vue'
import SFProductGallery from '~/components/product/detail/productGallery/SFProductGallery.vue'
import SFProductRecommendations from '~/components/product/SFProductRecommendations.vue'
import SFProductDetailPageLoadingState from '~/components/product/detail/SFProductDetailPageLoadingState.vue'
import SFRecentlyViewedProductsSlider from '~/components/product/SFRecentlyViewedProductsSlider.vue'
import {
  getCombineWithProductIds,
  useProductSeoData,
} from '#storefront-product-detail'
import {
  useBreadcrumbs,
  useGenderMenuItems,
  useRouteHelpers,
} from '~/composables'
import { generateProductSchema } from '#storefront-product-detail/utils/seo'
import SfProductDetailsLayout from '~/components/product/SFProductDetailsLayout.vue'
import SFProductDetails from '~/components/product/SFProductDetails.vue'
import { useTopNavigationMenu } from '~/composables/useTopNavigationMenu'
import { formatBreadcrumbs } from '~/utils/breadcrumbs'
import { normalizeWithoutSpecialCharacters } from '~/utils/stringNormalize'

const CURRENT_PRODUCT_DATA_KEY = 'PDP-currentProduct'

const { setCurrentMenu, currentMenu } = useTopNavigationMenu()

definePageMeta({
  validate(route: any) {
    // Parse URL segments: productName-color-id or productName-color-id/variantId
    const slug = Array.isArray(route.params.slug)
      ? route.params.slug[0]
      : route.params.slug
    if (!slug) {
      return false
    }

    const segments = slug.split('-')
    if (segments.length < 3) {
      return false
    }

    // Check if last segment is a number (product ID)
    const lastSegment = segments[segments.length - 1]
    return /^\d+$/.test(lastSegment)
  },
})

const route = useRoute()

// Parse URL segments to extract product info
const urlSegments = computed(() => {
  const slug = Array.isArray(route.params.slug)
    ? route.params.slug
    : [route.params.slug]
  return slug.filter(Boolean)
})

const productId = computed(() => {
  const mainSegment = urlSegments.value[0] || ''
  const segments = mainSegment.split('-')
  const lastSegment = segments[segments.length - 1]
  return parseInt(lastSegment)
})

const productAsyncData = await useProduct(
  {
    params: computed(() => ({
      id: productId.value,
      with: PRODUCT_DETAIL_WITH_PARAMS,
    })),
    options: {
      lazy: true,
      dedupe: 'defer',
    },
  },
  CURRENT_PRODUCT_DATA_KEY,
)

const { data: product, error } = productAsyncData

whenever(
  error,
  (err: any) => {
    throw createError({ ...err, fatal: true })
  },
  { immediate: true },
)
const {
  name,
  brand,
  description,
  longestCategoryList,
  variants,
  image,
  color,
  link,
} = useProductBaseInfo(product)

const recommendedProductIds = computed(() => {
  return getCombineWithProductIds(
    product.value?.advancedAttributes?.['combineWith'],
  )
})

const { getBreadcrumbsFromProductCategories } = useBreadcrumbs()

const breadcrumbs = computed(() =>
  getBreadcrumbsFromProductCategories(longestCategoryList.value ?? []),
)

const { getImage } = useImage()
const { $config } = useNuxtApp()
const { activeVariant } = useSizeSelector()

const productInfo = computed(() => ({
  name: name.value,
  brand: brand.value,
  productDescription: description.value,
  variants: variants.value.map((variant: any) => {
    const size = getFirstAttributeValue(variant.attributes, 'size')?.label || ''
    const schemaUrl = `${$config.public.baseUrl}${link.value}/${variant.id}`

    const schema = generateProductSchema({
      productName: `${name.value}, ${color.value}`,
      variant,
      url: schemaUrl,
      size,
      image: image.value
        ? getImage(image.value?.hash, {
            sizes: '100vw',
            provider: 'scayle',
          }).url
        : '',
    })

    // Remove the variantId query parameter from the offers URL
    if (schema.offers) {
      if (Array.isArray(schema.offers)) {
        schema.offers = schema.offers.map((offer: any) => ({
          ...offer,
          url: offer.url ? offer.url.split('?')[0] : offer.url,
        }))
      } else if (schema.offers.url) {
        schema.offers.url = schema.offers.url.split('?')[0]
      }
    }

    // Remove query params from the main URL if present
    if (schema.url) {
      schema.url = schema.url.split('?')[0]
    }

    return schema
  }),
  productId: product.value?.id || 0,
  color: color.value,
  variesBy: variants.value.length > 1 ? ['https://schema.org/size'] : undefined,
}))

const currentShop = useCurrentShop()
const { isValidGender } = useGenderMenuItems()
const formattedBreadcrumbs = computed(() =>
  formatBreadcrumbs(
    breadcrumbs.value,
    currentShop.value.path || 'en-eu',
    isValidGender,
  ),
)
const { getLocalizedRoute } = useRouteHelpers()
// SEO
const { robots, title, productJsonLd, productBreadcrumbJsonLd } =
  useProductSeoData(
    formattedBreadcrumbs,
    { baseUrl: $config.public.baseUrl, fullPath: route.fullPath },
    productInfo,
  )

const i18n = useI18n()

// Get the canonical URL without parameters
const canonicalUrl = computed(() =>
  sanitizeCanonicalURL(
    `${$config.public.baseUrl}${getLocalizedRoute(link.value ?? '')}`,
    [],
  ),
)

const twitterDomain = computed(() => {
  try {
    const url = new URL($config.public.baseUrl)
    return url.hostname
  } catch {
    return 'snipes.com' // fallback
  }
})

// Get the main product image URL
const ogImageUrl = computed(() => {
  if (!image.value?.hash) {
    return undefined
  }

  return getImage(image.value.hash, {
    provider: 'scayle',
    modifiers: {
      f: 'avif',
    },
  }).url
})

useSeoMeta({
  title,
  description: () =>
    product.value
      ? i18n.t('product_detail_page.meta.description', {
          productName: name.value,
          shopName: $config.public.shopName,
        })
      : undefined,
  robots,
  // Open Graph tags
  ogUrl: canonicalUrl,
  ogTitle: () => (title.value ? `${title.value} | SNIPES` : undefined),
  ogDescription: () =>
    product.value
      ? i18n.t('product_detail_page.meta.description', {
          productName: name.value,
          shopName: $config.public.shopName,
        })
      : undefined,
  ogImage: ogImageUrl,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  // Twitter tags
  twitterCard: 'summary_large_image',
  twitterDomain: twitterDomain,
  twitterUrl: canonicalUrl,
  twitterTitle: () => (title.value ? `${title.value} | SNIPES` : undefined),
  twitterDescription: () =>
    product.value
      ? i18n.t('product_detail_page.meta.description', {
          productName: name.value,
          shopName: $config.public.shopName,
        })
      : undefined,
  twitterImage: ogImageUrl,
})

useHead(() => {
  return {
    link: [
      {
        rel: 'canonical',
        key: 'canonical',
        href: canonicalUrl.value,
      },
    ],
  }
})

useJsonld(() => [productBreadcrumbJsonLd.value, productJsonLd.value])
definePageMeta({
  pageType: 'product_detail_page',
  key: 'PDP',
  contentType: 'Product Detail Page',
  SnPageType: 'Product',
})

const setCurrentGenderByProduct = (product: any) => {
  if (!product) {
    return
  }

  const gender = getGenderFromProduct(product)

  if (gender && gender !== currentMenu.value) {
    setCurrentMenu(gender)
  }
}

const getGenderFromProduct = (product: any): string | null | undefined => {
  const genderAttr = getFirstAttributeValue(product.attributes, 'genders')

  if (!genderAttr || genderAttr?.label === 'unisex') {
    return
  }

  const genders = i18n.tm('genders') // if there's only one gender, we can use the label
  const genderObj = (genders as Record<string, any>)[genderAttr.label]

  return genderObj
    ? normalizeWithoutSpecialCharacters(genderObj.body?.static)
    : undefined
}

watch(
  () => product.value,
  (newProduct) => {
    setCurrentGenderByProduct(newProduct)
  },
)

// Tracking
onMounted(async () => {
  setCurrentGenderByProduct(product.value)

  let isTracked = false

  // Wait for the product with complete variant stock data to be loaded
  whenever(
    () => {
      const prod = product.value
      if (!prod?.variants?.length) {
        return false
      }

      // Check if variants have stock data loaded
      const hasStockData = prod.variants.some(
        (v: any) => v?.stock !== undefined,
      )
      return hasStockData ? prod : false
    },
    (loadedProduct: any) => {
      if (!isTracked) {
        isTracked = true
        useTrackingEvents().trackViewItem({
          product: loadedProduct,
          variant: activeVariant.value,
        })
      }
    },
    { immediate: true, once: true },
  )

  // Fallback: if stock data doesn't load within reasonable time, track anyway
  setTimeout(() => {
    if (product.value && !isTracked) {
      isTracked = true
      useTrackingEvents().trackViewItem({
        product: product.value,
        variant: activeVariant.value,
      })
    }
  }, 2000)
})

// Clear the data when leaving the page
onUnmounted(() => {
  clearNuxtData(CURRENT_PRODUCT_DATA_KEY)
})

const { addProductId, loadMissingProducts } = useRecentlyViewedProducts()
// Because we do define the same key `PDP` in the `definePageMeta` hook, `onMounted` hook is not necessarily called when the PDP changes.
// Therefore we update the recently viewed products every time the current product changes.
// This also avoids the `beforeunload` event, which does not properly work on IOS.
whenever(
  product,
  async () => {
    if (import.meta.server) {
      return
    }
    // We load the products before adding the current product to the list.
    // This is to avoid showing the current product as first item in the recently viewed products slider.
    await loadMissingProducts()
    addProductId(productId.value)
  },
  { immediate: true },
)
</script>
